import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchBook, resolveCover } from "@/lib/api";
import { toast } from "sonner";
import ReviewSection from "@/components/ReviewSection";
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  BookOpen,
  Clock,
  Feather,
} from "lucide-react";

export const BookDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchBook(id)
      .then(setBook)
      .catch(() => {
        toast.error("Book not found");
        navigate("/");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleBuy = () => {
    if (!book?.buy_link) {
      toast.error("This book doesn't have a purchase link yet.");
      return;
    }
    window.open(book.buy_link, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-40 gap-3 text-[var(--bw-secondary)]">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="font-mono-plex text-sm">loading book</span>
      </div>
    );
  }

  if (!book) return null;

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 pt-10">
      <Link
        to="/"
        data-testid="back-to-catalog"
        className="inline-flex items-center gap-2 text-sm text-[var(--bw-secondary)] hover:text-[var(--bw-primary)] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to catalog
      </Link>

      <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Cover */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div className="relative overflow-hidden border border-[var(--bw-border)] aspect-[3/4] bg-[var(--bw-surface)]">
              <img
                src={resolveCover(book.cover_url)}
                alt={book.title}
                className="w-full h-full object-cover"
                data-testid="book-cover-image"
                onError={(e) => {
                  e.currentTarget.src =
                    "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?crop=entropy&cs=srgb&fm=jpg&w=800&q=85";
                }}
              />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="border border-[var(--bw-border)] p-3">
                <BookOpen className="w-4 h-4 mx-auto text-[var(--bw-accent)]" />
                <p className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)] mt-2">
                  Pages
                </p>
                <p className="font-serif-display text-xl">5</p>
              </div>
              <div className="border border-[var(--bw-border)] p-3">
                <Clock className="w-4 h-4 mx-auto text-[var(--bw-accent)]" />
                <p className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)] mt-2">
                  Read
                </p>
                <p className="font-serif-display text-xl">12m</p>
              </div>
              <div className="border border-[var(--bw-border)] p-3">
                <Feather className="w-4 h-4 mx-auto text-[var(--bw-accent)]" />
                <p className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)] mt-2">
                  Format
                </p>
                <p className="font-serif-display text-xl">PDF</p>
              </div>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-7">
          <span
            data-testid="book-category-badge"
            className="font-mono-plex text-[10px] uppercase tracking-[0.3em] px-2 py-1 border border-[var(--bw-primary)]/30 rounded-full"
          >
            {book.category || "General"}
          </span>
          <h1
            data-testid="book-title"
            className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-4"
          >
            {book.title}
          </h1>
          {book.author && (
            <p
              data-testid="book-author"
              className="mt-4 text-lg md:text-xl text-[var(--bw-secondary)] font-serif-display italic"
            >
              by {book.author}
            </p>
          )}

          <div className="mt-8 flex items-baseline gap-4 flex-wrap">
            <span
              data-testid="book-price-display"
              className="font-mono-plex text-3xl md:text-4xl"
            >
              ${book.price?.toFixed(2)}
            </span>
            {book.original_price && book.original_price > book.price && (
              <>
                <span
                  data-testid="book-original-price"
                  className="text-lg text-[var(--bw-secondary)] line-through font-mono-plex"
                >
                  ${book.original_price.toFixed(2)}
                </span>
                <span
                  data-testid="book-discount-badge"
                  className="font-mono-plex text-xs uppercase tracking-widest text-white bg-[var(--bw-accent)] px-2 py-1 rounded-full"
                >
                  {Math.round(
                    (1 - book.price / book.original_price) * 100
                  )}
                  % off
                </span>
              </>
            )}
          </div>

          {book.tags && book.tags.length > 0 && (
            <div
              data-testid="book-tags"
              className="mt-6 flex flex-wrap gap-2"
            >
              {book.tags.map((t) => (
                <span
                  key={t}
                  data-testid={`book-tag-${t}`}
                  className="px-3 py-1 rounded-full text-xs border border-[var(--bw-border)] text-[var(--bw-secondary)] font-mono-plex"
                >
                  #{t}
                </span>
              ))}
            </div>
          )}

          <div className="mt-8">
            <button
              data-testid="buy-now-button"
              onClick={handleBuy}
              disabled={!book.buy_link}
              className="group inline-flex items-center gap-3 rounded-full bg-[var(--bw-primary)] text-[var(--bw-bg)] px-8 py-4 text-base md:text-lg hover:bg-[var(--bw-accent)] transition-colors focus-visible:ring-2 focus-visible:ring-[var(--bw-accent)] outline-none disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {book.buy_link ? (
                <>
                  Buy now
                  <ExternalLink className="w-4 h-4" />
                </>
              ) : (
                <>Coming soon</>
              )}
            </button>
            {!book.buy_link && (
              <p className="mt-3 text-xs text-[var(--bw-secondary)]">
                A checkout link hasn&apos;t been added for this book yet.
              </p>
            )}
          </div>

          <div className="mt-14 border-t border-[var(--bw-border)] pt-8">
            <p className="font-mono-plex text-xs uppercase tracking-[0.35em] text-[var(--bw-secondary)] mb-4">
              About this book
            </p>
            <p
              data-testid="book-description"
              className="text-base md:text-lg leading-relaxed whitespace-pre-line"
            >
              {book.description}
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            <div className="border border-[var(--bw-border)] p-6">
              <p className="font-mono-plex text-xs uppercase tracking-widest text-[var(--bw-secondary)]">
                What&apos;s inside
              </p>
              <ul className="mt-4 space-y-2 text-sm md:text-base">
                <li>· Every chapter&apos;s core idea</li>
                <li>· Frameworks &amp; step-by-step models</li>
                <li>· Memorable quotes &amp; takeaways</li>
                <li>· 2-minute recap on page 5</li>
              </ul>
            </div>
            <div className="border border-[var(--bw-border)] p-6 bg-[var(--bw-primary)] text-[var(--bw-bg)]">
              <p className="font-mono-plex text-xs uppercase tracking-widest text-white/60">
                No-risk promise
              </p>
              <p className="mt-4 font-serif-display text-2xl leading-snug">
                Not useful? Email us — we&apos;ll refund the coffee-money.
              </p>
            </div>
          </div>
        </div>
      </div>

      <ReviewSection bookId={book.id} />
    </main>
  );
};

export default BookDetail;
